import { LockKeyhole, Send } from "lucide-react";
import { useEffect, useState, type FormEvent } from "react";
import { money } from "../../shared/domain";
import type { EarnSnapshot, Redemption } from "../../shared/types";
import { Spinner } from "../components/Spinner";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth-context";

export function CashOutPage() {
  const { session } = useAuth();
  const [earn, setEarn] = useState<EarnSnapshot>();
  const [redemptions, setRedemptions] = useState<Redemption[]>([]);
  const [enabled, setEnabled] = useState(false);
  const [amount, setAmount] = useState(500);
  const [email, setEmail] = useState(session?.user.email || "");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { void Promise.all([api.earn(), api.redemptions()]).then(([snapshot, data]) => { setEarn(snapshot); setEnabled(data.enabled); setRedemptions(data.redemptions); }).catch((reason) => setError(String(reason))); }, []);
  const submit = async (event: FormEvent) => {
    event.preventDefault(); setBusy(true); setError("");
    try {
      const item = await api.requestRedemption(amount, email);
      setRedemptions((current) => [item, ...current]);
      setSuccess(`${money(amount)} is now reserved while we review your request.`);
      setEarn(await api.earn());
    } catch (reason) { setError(reason instanceof Error ? reason.message : "Unable to request cash out."); } finally { setBusy(false); }
  };
  if (!earn && !error) return <Spinner label="Loading cash out" />;
  return (
    <div className="cashout-page page-width">
      <header className="page-title"><p className="eyebrow">YOUR REWARDS</p><h1>Cash out</h1><p>Request only from your available, unreserved balance.</p></header>
      <div className="cashout-grid">
        <section className="cashout-card">
          <p className="eyebrow">AVAILABLE</p><div className="cashout-balance">{earn ? money(earn.balance.availableCents) : "—"}</div>
          {!enabled ? <div className="feature-locked"><LockKeyhole /><h2>Cash out is coming soon.</h2><p>Your earnings remain in your Bucks account. Requests will open after payout and operational review is complete.</p></div> : (
            <form onSubmit={submit}>
              <fieldset><legend>Choose an amount</legend><div className="amount-options">{[500, 1000].map((value) => <button type="button" key={value} className={amount === value ? "selected" : ""} disabled={!earn || value > earn.balance.availableCents} onClick={() => setAmount(value)}>{money(value)}</button>)}</div></fieldset>
              <label>Payout email<input type="email" required value={email} onChange={(event) => setEmail(event.target.value)} /></label>
              {success && <div className="form-success" role="status">{success}</div>}{error && <div className="form-error" role="alert">{error}</div>}
              <button className="button" disabled={busy || !earn || amount > earn.balance.availableCents}>{busy ? "Requesting…" : `Request ${money(amount)}`}<Send /></button>
              <small>Requests are manually reviewed. A request reserves funds so they can't be requested twice.</small>
            </form>
          )}
        </section>
        <section className="payout-history"><h2>Requests</h2>{redemptions.length ? redemptions.map((item) => <article key={item.id}><div><strong>{money(item.amountCents)}</strong><span>{item.payoutEmailMasked}</span></div><span className={`state-pill ${item.status}`}>{item.status}</span></article>) : <p>No cash-out requests yet.</p>}</section>
      </div>
    </div>
  );
}
